/** Matches jssip RTCSession.C (numeric session.status). */
export const JSSIP_SESSION_STATUS_WAITING_FOR_ANSWER = 4;
export const JSSIP_SESSION_STATUS_ANSWERED = 5;
export const JSSIP_STATUS_CANCELED = 7;
export const JSSIP_STATUS_TERMINATED = 8;

const TERMINAL_SESSION_STRINGS = new Set([
  'ended',
  'failed',
  'terminated',
  'canceled'
]);

/**
 * @param {unknown} state
 * @returns {boolean}
 */
export function isTerminalSessionState(state) {
  if (typeof state === 'number') {
    return state === JSSIP_STATUS_CANCELED || state === JSSIP_STATUS_TERMINATED;
  }
  if (typeof state !== 'string') return false;
  return TERMINAL_SESSION_STRINGS.has(state.trim().toLowerCase());
}

/**
 * @param {RTCPeerConnection|null|undefined} pc
 * @returns {boolean}
 */
export function isPeerConnectionDead(pc) {
  if (!pc) return false;
  return pc.connectionState === 'closed'
    || pc.connectionState === 'failed'
    || pc.iceConnectionState === 'closed'
    || pc.iceConnectionState === 'failed';
}

/**
 * @param {import('jssip').RTCSession|null|undefined} session
 * @returns {boolean}
 */
export function isAgentCallDead(session) {
  if (!session) return true;
  if (session.isEnded?.()) return true;
  if (isTerminalSessionState(session.status)) return true;
  return isPeerConnectionDead(session.connection);
}

/**
 * @param {import('jssip').RTCSession|null|undefined} session
 * @returns {boolean}
 */
export function isAgentSessionAlreadyAnswered(session) {
  if (!session || isAgentCallDead(session)) return false;
  if (session.isEstablished?.()) return true;
  const { status } = session;
  if (status === JSSIP_SESSION_STATUS_ANSWERED) return true;
  if (status === 'confirmed' || status === 'answered') return true;
  return false;
}

/**
 * @param {import('jssip').RTCSession|null|undefined} session
 * @returns {boolean}
 */
export function isAgentSessionWaitingForAnswer(session) {
  if (!session) return false;
  const { status } = session;
  return status === JSSIP_SESSION_STATUS_WAITING_FOR_ANSWER
    || status === 'waiting_for_answer';
}

/**
 * @param {import('jssip').RTCSession|null|undefined} session
 * @returns {boolean}
 */
export function isPeerConnectionIceReady(session) {
  const pc = session?.connection;
  if (!pc) return false;
  const iceOk = pc.iceConnectionState === 'connected'
    || pc.iceConnectionState === 'completed';
  const connOk = pc.connectionState === 'connected';
  return iceOk || connOk;
}

/**
 * @param {import('jssip').RTCSession|null|undefined} session
 * @returns {boolean}
 */
export function isPeerConnectionMediaReady(session) {
  const pc = session?.connection;
  if (!pc) return false;
  if (!isPeerConnectionIceReady(session)) return false;
  const receivers = pc.getReceivers?.() ?? [];
  if (
    receivers.some(
      (entry) => entry.track?.kind === 'audio' && entry.track.readyState === 'live'
    )
  ) {
    return true;
  }
  const senders = pc.getSenders?.() ?? [];
  return senders.some(
    (entry) => entry.track?.kind === 'audio' && entry.track.readyState === 'live'
  );
}
